import React from 'react';
import { Box, Typography, CircularProgress, Chip, Stack, useTheme } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { font } from '../../utils/fontHelper';

export interface NotificationSendPreviewPanelProps {
  title: string;
  message: string;
  /** Local object URL or remote image URL for push / in-app preview */
  imagePreviewUrl?: string | null;
  /** Human-readable navigation summary */
  navigationSummary: string | null;
  recipientLines: string[];
  recipientLoading?: boolean;
  isDark: boolean;
}

/**
 * Recipients summary + stylized push tray + in-app notification row previews.
 */
export const NotificationSendPreviewPanel: React.FC<NotificationSendPreviewPanelProps> = ({
  title,
  message,
  imagePreviewUrl,
  navigationSummary,
  recipientLines,
  recipientLoading,
  isDark,
}) => {
  const theme = useTheme();
  const pushTitle = title.trim() || 'Title';
  const pushBody = message.trim() || 'Message';
  const lockMuted = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';

  return (
    <Stack spacing={3}>
      <Box>
        <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 12, color: 'text.secondary', mb: 1, letterSpacing: 0.5 }}>
          RECIPIENTS
        </Typography>
        {recipientLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontFamily: font(), fontSize: 14, color: 'text.secondary' }}>Counting recipients…</Typography>
          </Box>
        ) : (
          recipientLines.map((line, i) => (
            <Typography key={i} sx={{ fontFamily: font(), fontSize: 14, color: 'text.primary', mb: 0.5 }}>
              {line}
            </Typography>
          ))
        )}
        {navigationSummary && (
          <Chip
            label={navigationSummary}
            size="small"
            sx={{ mt: 1, fontFamily: font(), fontSize: 12 }}
            variant="outlined"
          />
        )}
      </Box>

      <Box>
        <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 12, color: 'text.secondary', mb: 1, letterSpacing: 0.5 }}>
          PUSH NOTIFICATION (LOCK SCREEN / TRAY)
        </Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 11, color: 'text.secondary', mb: 1.5 }}>
          Layout varies by OS. Title ~30 characters recommended on iOS; image shows on Android when configured.
        </Typography>
        <Box
          sx={{
            borderRadius: 3,
            p: 2,
            background: isDark
              ? 'linear-gradient(180deg, rgba(60,60,70,0.9) 0%, rgba(30,30,36,1) 100%)'
              : 'linear-gradient(180deg, rgba(230,230,235,1) 0%, rgba(200,200,210,0.95) 100%)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          }}
        >
          <Typography sx={{ fontFamily: font(), fontSize: 13, color: lockMuted, textAlign: 'center', mb: 2 }}>
            9:41
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              p: 1.5,
              borderRadius: 2,
              bgcolor: isDark ? 'rgba(40,40,44,0.92)' : 'rgba(255,255,255,0.88)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.25,
                bgcolor: '#7D7AFF',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontFamily: font(),
                fontWeight: 800,
                fontSize: 18,
              }}
            >
              W
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 14, lineHeight: 1.3, mb: 0.25 }}>
                {pushTitle}
              </Typography>
              <Typography
                sx={{
                  fontFamily: font(),
                  fontSize: 13,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {pushBody}
              </Typography>
            </Box>
          </Box>
          {imagePreviewUrl && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontFamily: font(), fontSize: 11, color: 'text.secondary', mb: 0.75 }}>
                Rich image (Android typical)
              </Typography>
              <Box
                component="img"
                src={imagePreviewUrl}
                alt=""
                sx={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 2, border: 1, borderColor: 'divider' }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 12, color: 'text.secondary', mb: 1, letterSpacing: 0.5 }}>
          IN-APP NOTIFICATIONS
        </Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 11, color: 'text.secondary', mb: 1.5 }}>
          Matches the announcements row in the notifications list (title + message).
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#7D7AFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <NotificationsIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 14, lineHeight: 1.4 }}>
              {pushTitle}: {pushBody}
            </Typography>
            <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
              Just now
            </Typography>
          </Box>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#7D7AFF',
              flexShrink: 0,
              mt: 0.75,
            }}
          />
        </Box>
        {imagePreviewUrl && (
          <Typography sx={{ fontFamily: font(), fontSize: 11, color: 'text.secondary', mt: 1 }}>
            Image appears in the in-app notification detail when supported.
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
